import * as Clipboard from 'expo-clipboard';
import { router } from 'expo-router';
import { ArrowRight, Check, Share2 } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { Image, StyleSheet, Text as RNText, View } from 'react-native';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Overline } from '@/components/Overline';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { campaignShareUrl } from '@/lib/campaignShare';
import { authStore } from '@/stores/auth.store';
import type { GiftClaim } from '@/types/gifting';

const collage = require('@/assets/images/gifting/gift-collage.jpg');

/**
 * Home-screen campaign card. Pinned slot: immediately below the cash-out /
 * earnings hero, above the stat row (the prototype's deliberate
 * high-visibility placement — this card is the whole campaign's conversion
 * surface for existing users).
 *
 * Two states:
 *   unclaimed → pitch + "Claim your gift"
 *   claimed   → gift recap + "View my gift" (kept even after the campaign
 *               flag is unset — the claim payload carries the product
 *               snapshot)
 *
 * An observer because it reads the campaign name off the auth store — a
 * non-observer child reading store fields its parent doesn't track is the
 * exact reactivity gap the shipping-form review caught.
 */
export const GiftingHomeCard = observer(function GiftingHomeCard({
  claim,
  onLinkCopied,
}: {
  claim: GiftClaim | null | undefined;
  /** Host screen shows its toast — the card is inside a Card, which would clip an absolute pill. */
  onLinkCopied?: () => void;
}) {
  const copy = campaignCopy(authStore.user?.gifting_copy);

  // Copy-direct, not the share sheet (Jeff, 2026-08-07): one tap, link on
  // the clipboard, toast confirms. The full share sheet still lives on the
  // gift screen.
  async function copyLink() {
    try {
      await Clipboard.setStringAsync(campaignShareUrl(authStore.user?.ref_id));
      onLinkCopied?.();
    } catch {
      // Clipboard unavailable — don't toast "copied" when nothing happened.
    }
  }

  if (claim) {
    return (
      <Card testID="gifting-home-card-claimed">
        <Overline>{copy.name}</Overline>
        <View style={styles.claimRow}>
          <View style={styles.claimImageWrap}>
            {claim.product.image_url ? (
              <Image
                source={{ uri: claim.product.image_url }}
                style={styles.claimImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.claimImageFallback}>
                <RNText style={styles.claimImageFallbackText}>
                  {claim.product.name.slice(0, 1)}
                </RNText>
              </View>
            )}
          </View>
          <View style={styles.claimMeta}>
            <Text variant="bodySemiBold">{claim.product.name}</Text>
            <View style={styles.securedRow}>
              <Check color={colors.accentText} size={16} />
              <Text variant="caption" color="accentText">
                Gift claimed
              </Text>
            </View>
          </View>
        </View>
        {/* Share rides the home card too (2026-08-07): the referral loop is
            the campaign's growth engine, so the link is one tap from home,
            not buried behind "View my gift". */}
        <View style={styles.claimedButtons}>
          <Button
            label="View my gift"
            variant="outline"
            size="sm"
            onPress={() => router.push('/season-of-gifting/secured')}
            style={styles.claimedButton}
          />
          <Button
            label="Share link"
            size="sm"
            leadingIcon={Share2}
            onPress={() => void copyLink()}
            style={styles.claimedButton}
          />
        </View>
      </Card>
    );
  }

  return (
    <Card testID="gifting-home-card">
      <View style={styles.pitchRow}>
        <View style={styles.pitchText}>
          <Overline>{copy.name}</Overline>
          <Text variant="bodySemiBold" style={styles.pitchHeadline}>
            Your first NIL gift of the semester is here.
          </Text>
          <Text variant="caption" color="textMuted" style={styles.pitchSub}>
            Pick one free product. No required post.
          </Text>
        </View>
        <Image source={collage} style={styles.pitchImage} resizeMode="cover" />
      </View>
      <Button
        label="Claim your gift"
        trailingIcon={ArrowRight}
        onPress={() => router.push('/season-of-gifting')}
        style={styles.cta}
      />
    </Card>
  );
});

const styles = StyleSheet.create({
  pitchRow: {
    flexDirection: 'row',
    gap: spacing.base,
    alignItems: 'flex-start',
  },
  pitchText: {
    flex: 1,
  },
  pitchHeadline: {
    marginTop: spacing.sm,
  },
  pitchSub: {
    marginTop: spacing.xs,
  },
  pitchImage: {
    width: 88,
    height: 88,
    borderRadius: radii.lg,
    backgroundColor: colors.background,
  },
  claimRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.base,
    marginTop: spacing.md,
  },
  claimImageWrap: {
    width: 56,
    height: 56,
    borderRadius: radii.base,
    overflow: 'hidden',
    backgroundColor: colors.background,
  },
  claimImage: {
    width: '100%',
    height: '100%',
  },
  claimImageFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  claimImageFallbackText: {
    fontFamily: fonts.display,
    fontSize: fontSizes.xl,
    color: colors.deepGreen,
  },
  claimMeta: {
    flex: 1,
    gap: spacing.xs,
  },
  securedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  cta: {
    marginTop: spacing.base,
  },
  claimedButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.base,
  },
  claimedButton: {
    flex: 1,
  },
});
